import {
  avatarSlotRecipe,
  dialogSlotRecipe as baseDialogSlotRecipe,
  drawerSlotRecipe as baseDrawerSlotRecipe,
  fieldSlotRecipe as baseFieldSlotRecipe,
  listSlotRecipe as baseListSlotRecipe,
  menuSlotRecipe as baseMenuSlotRecipe,
  popoverSlotRecipe as basePopoverSlotRecipe,
  tableSlotRecipe as baseTableSlotRecipe,
  tagSlotRecipe as baseTagSlotRecipe,
  tooltipSlotRecipe as baseTooltipSlotRecipe,
  fieldsetSlotRecipe,
  nativeSelectSlotRecipe,
  progressSlotRecipe,
  stepsSlotRecipe,
  tabsSlotRecipe,
  toastSlotRecipe,
} from '@chakra-ui/react/theme';

import { formSelectionLabel, formSelectionSurface } from '../form-selection';
import { checkboxCardSlotRecipe } from './checkbox-card';
import {
  checkboxSlotRecipe,
  radioGroupSlotRecipe,
  sliderSlotRecipe,
  switchSlotRecipe,
} from './choice-controls';
import { comboboxSlotRecipe } from './combobox';
import { noticeSlotRecipe } from './notice';
import { radioCardSlotRecipe } from './radio-card';
import { selectSlotRecipe } from './select';

// ── Slot recipe overrides ──────────────────────────────────────────

const drawerSlotRecipe = {
  ...baseDrawerSlotRecipe,
  base: {
    ...baseDrawerSlotRecipe.base,
    content: { ...baseDrawerSlotRecipe.base?.content, bg: 'bg.surface' },
  },
};

// Хром шапки живёт здесь, а не в каждой модалке. Раньше вариантов набралось пять:
// серая плашка bg.emphasized, фиолетово-красный градиент, textStyle="subtitle" без
// фона, голый Dialog.Header и онбординг со своим mt. Плашка мешала больше всех — она
// на 10% светлее поверхности окна, поэтому читалась как случайная полоса, а на
// телефоне ещё и оказывалась впритык к ручке шторки. Фона у шапки нет вовсе:
// заголовок отделён волосяной линией, а брендированные модалки красят её через accent
// (см. DialogHeader).
//
// position: relative нужен крестику — он позиционируется внутри шапки и потому всегда
// оптически на одной линии с заголовком, независимо от того, есть иконка или нет.
// Раньше каждая модалка подбирала ему top/right вручную, и значения разъехались.
const dialogSlotRecipe = {
  ...baseDialogSlotRecipe,
  base: {
    ...baseDialogSlotRecipe.base,
    // Радиус задан здесь, а не в каждом окне. Раньше три модалки писали его руками и
    // разошлись на две формы: 12px у одних, 16px у других. Окно - поверхность, значит `l3`,
    // и на телефоне шторка всё равно перебивает его нулём (см. sheet ниже).
    content: { ...baseDialogSlotRecipe.base?.content, bgColor: 'bg.surface', borderRadius: 'l3' },
    header: {
      ...baseDialogSlotRecipe.base?.header,
      position: 'relative',
      alignItems: 'center',
      gap: '3',
      flexShrink: 0,
      // pt/pb, а не py: в базовом рецепте уже лежат отдельные pt и pb, и шорткат
      // перебивал бы их только за счёт порядка ключей.
      px: '6',
      pt: '4',
      pb: '4',
      // Место под крестик, чтобы длинный заголовок не заезжал под него.
      pe: '14',
      borderBottom: '1px solid',
      borderColor: 'border.subtle',
    },
    title: { ...baseDialogSlotRecipe.base?.title, textStyle: 'subtitle' },
    body: { ...baseDialogSlotRecipe.base?.body, pt: '5' },
    closeTrigger: { ...baseDialogSlotRecipe.base?.closeTrigger, top: '3', insetEnd: '3' },
  },
};

const glassOverlay = {
  bg: 'bg.overlay',
  boxShadow: 'md',
  backdropFilter: 'blur(14px) saturate(180%)',
};

/*
  Всплывающие слои — меню и поповеры — стоят НАД интерфейсом, а не над артом, поэтому фон у них
  сплошной.

  Раньше они брали то же стекло, что и тост: `bg.overlay` — та же плита, но с альфой 0.5 — плюс
  блюр. Сквозь пункты проступало то, что оказалось под ними: в меню аватара на телефоне белая
  кнопка баннера «Verify your email» ложилась ровно под «Log out», в меню чата просвечивали
  кнопка «Join» и пузыри переписки. Блюр размывает, но не гасит: яркое пятно остаётся ярким и
  спорит с текстом пункта.

  Правило дизайн-системы ровно об этом: размытие — только когда за элементом действительно
  картинка. Кнопки над обложкой заявки берут `bg.overlay` напрямую и здесь не участвуют, тост
  всплывает поверх ленты с обложками и стекло сохраняет.
*/
const overlaySurface = {
  bg: 'bg.surface',
  boxShadow: 'md',
};

const menuSlotRecipe = {
  ...baseMenuSlotRecipe,
  base: {
    ...baseMenuSlotRecipe.base,
    content: { ...baseMenuSlotRecipe.base?.content, ...overlaySurface },
    item: {
      ...baseMenuSlotRecipe.base?.item,
      '@media (any-pointer: coarse)': { minHeight: '44px' },
      bg: 'transparent',
      // `bg.muted`, как у строки списка и пункта бокового меню: всё, что светлеет под
      // курсором, светлеет одной ступенью. Здесь стояло `whiteAlpha.300` — 16% белого против
      // 6% везде, и пункт меню прыгал под курсором втрое сильнее строки рядом.
      _hover: { bg: 'bg.muted' },
      // Иконка пункта красится отдельно от его текста — сплошным fg.icon вместо прозрачного
      // цвета, который она наследовала. Ступени fg.* полупрозрачны, а иконка lucide это
      // несколько отдельных штрихов в одной группе: там, где они пересекаются (диагональ и
      // окружность у «заблокировать», плечи и круг у «открыть профиль»), альфа складывается
      // и стык уезжает к белому — контур выглядит пятнистым.
      //
      // Красится ТОЛЬКО иконка, не весь пункт: `color` на самом item перекрасил бы и текст
      // меню в серый. Пункты, которые задают себе цвет осмысленно (оранжевый у
      // «разблокировать», fg.warning в шапке чата), ставят его инлайном — инлайн-стиль
      // перебивает рецепт, и там иконка по-прежнему идёт за текстом.
      //
      // Без `>`: пункт бывает `asChild` с Link внутри (RowMenu, PostMenu), и тогда иконка
      // лежит на уровень глубже.
      '& svg': { color: 'fg.icon' },
    },
  },
};

const popoverSlotRecipe = {
  ...basePopoverSlotRecipe,
  base: {
    ...basePopoverSlotRecipe.base,
    content: { ...basePopoverSlotRecipe.base?.content, ...overlaySurface },
    arrowTip: {
      ...basePopoverSlotRecipe.base?.arrowTip,
      bgColor: 'transparent !important',
    },
  },
};

/*
  Подсказка не была подключена к теме вовсе: в теме про неё был только z-index. Из-за этого
  она выходила голым текстом поверх страницы - замерено на живом элементе: фон прозрачный,
  отступов нет, скругления нет, тени нет. Поверх ленты с обложками такое не читается.

  Берём стоковый рецепт (он полный) и меняем в нём одно: цвет. Сток красит подсказку
  инвертированно - светлая плашка с тёмным текстом, - а все всплывающие слои у нас тёмные:
  меню и поповер стоят на `bg.surface`. Светлая подсказка среди них читалась бы как чужая.
*/
const tooltipSlotRecipe = {
  ...baseTooltipSlotRecipe,
  base: {
    ...baseTooltipSlotRecipe.base,
    content: {
      ...baseTooltipSlotRecipe.base?.content,
      '--tooltip-bg': 'colors.bg.surface',
      color: 'fg',
      boxShadow: 'md',
    },
  },
};

const tagSlotRecipe = {
  ...baseTagSlotRecipe,
  base: {
    ...baseTagSlotRecipe.base,
    /*
      Заливку здесь НЕ задаём, хотя и хотелось: `base` сливается с вариантом в один объект, а
      вариант по умолчанию (`surface`) ставит сокращённый `bg: colorPalette.subtle`. Сокращённое
      свойство идёт в CSS позже и перебивает `bgColor`, так что правило отсюда просто не
      применяется — тег рендерился bg.card вместо bg.emphasized. Цвет остаётся у вызывающих;
      чтобы увести его в тему, нужно менять сам вариант, а это заодно обесцветит теги с
      `colorPalette`, которые на этом варианте и держатся.
    */
    root: { ...baseTagSlotRecipe.base?.root, borderRadius: 'full' },
    startElement: {
      ...baseTagSlotRecipe.base?.startElement,
      pointerEvents: 'none',
    },
    label: {
      ...baseTagSlotRecipe.base?.label,
      display: 'flex',
      whiteSpace: 'nowrap',
      alignItems: 'center',
    },
  },
  variants: {
    ...baseTagSlotRecipe.variants,
    variant: {
      ...baseTagSlotRecipe.variants?.variant,
      selection: {
        root: {
          ...formSelectionSurface,
          // Inset outline preserves the 44px remove target without adding to the tag height.
          boxShadow: 'selectionRing',
          _disabled: { opacity: 0.5 },
        },
      },
    },
    size: {
      ...baseTagSlotRecipe.variants?.size,
      form: {
        root: { minH: '11', px: 3, py: 0, gap: 1.5 },
        label: { ...formSelectionLabel },
      },
      // 28px и caption — та же геометрия и типографика, что у Chip variant="fact" size="md"
      // (theme/recipes/chip.ts): обе серые пилюли встречаются на одной странице (теги заявки
      // рядом с фактами об игроке) и до этого читались как два разных дизайна — 32px/body
      // против 24px/caption. Тон остаётся разным по смыслу: у тега bg.emphasized («по этому
      // ищут»), у чипа bg.card (тихий факт о человеке).
      lg: {
        ...baseTagSlotRecipe.variants?.size?.lg,
        root: {
          ...baseTagSlotRecipe.variants?.size?.lg?.root,
          minHeight: 7,
          px: 3,
          boxShadow: 'none',
        },
        label: {
          ...baseTagSlotRecipe.variants?.size?.lg?.label,
          // Вес 600, а не 500: на 12px тег заявки — элемент сканирования, и на приглушённом
          // фоне карточки он с весом 500 читался блёкло рядом с именем игры.
          textStyle: 'label',
        },
      },
    },
  },
};

// Подписи полей раньше приходили из библиотечного рецепта: sm/500. Все остальные подписи
// на сайте — textStyle="title" (sm/600), поэтому на одном экране лейблы формы выглядели
// легче соседних заголовков (заметно в настройках профиля). Вес задаётся здесь, а не в
// каждом Field, чтобы формы во всех разделах говорили одним голосом.
const fieldSlotRecipe = {
  ...baseFieldSlotRecipe,
  base: {
    ...baseFieldSlotRecipe.base,
    label: {
      ...baseFieldSlotRecipe.base?.label,
      textStyle: 'title',
    },
  },
};

// Без этих двух рецептов List.Root и Table.* рендерились голыми ul/ol/table: preflight снимал
// маркеры (`list-style: none`), у таблиц не было ни рамок, ни шапки — так выглядели все статьи
// блога. Маркер fg.muted, а не библиотечный fg.subtle: 16% белого на фоне страницы не видно.
const listSlotRecipe = {
  ...baseListSlotRecipe,
  variants: {
    ...baseListSlotRecipe.variants,
    variant: {
      ...baseListSlotRecipe.variants?.variant,
      marker: {
        ...baseListSlotRecipe.variants?.variant?.marker,
        item: { _marker: { color: 'fg.muted' } },
      },
    },
  },
};

// Вариант line (он по умолчанию) красит строку в `bg`: поверх карточки это полосы цвета
// страницы (LfgSettingsTable). Строка прозрачная, фон задаёт контейнер.
const tableSlotRecipe = {
  ...baseTableSlotRecipe,
  variants: {
    ...baseTableSlotRecipe.variants,
    variant: {
      ...baseTableSlotRecipe.variants?.variant,
      line: { ...baseTableSlotRecipe.variants?.variant?.line, row: {} },
    },
  },
};

// ── Export ──────────────────────────────────────────────────────────

export const slotRecipes = {
  avatar: avatarSlotRecipe,
  checkbox: checkboxSlotRecipe,
  checkboxCard: checkboxCardSlotRecipe,
  dialog: dialogSlotRecipe,
  drawer: drawerSlotRecipe,
  field: fieldSlotRecipe,
  fieldset: fieldsetSlotRecipe,
  list: listSlotRecipe,
  menu: menuSlotRecipe,
  nativeSelect: nativeSelectSlotRecipe,
  notice: noticeSlotRecipe,
  popover: popoverSlotRecipe,
  progress: progressSlotRecipe,
  radioCard: radioCardSlotRecipe,
  radioGroup: radioGroupSlotRecipe,
  select: selectSlotRecipe,
  combobox: comboboxSlotRecipe,
  slider: sliderSlotRecipe,
  steps: stepsSlotRecipe,
  switch: switchSlotRecipe,
  table: tableSlotRecipe,
  tabs: tabsSlotRecipe,
  tag: tagSlotRecipe,
  tooltip: tooltipSlotRecipe,
  toast: {
    ...toastSlotRecipe,
    base: {
      ...toastSlotRecipe.base,
      root: { ...toastSlotRecipe.base?.root, ...glassOverlay },
    },
  },
};
